import { useEffect, useRef, useState } from "react";
import "./LiveUpdate.css";

// "New photos added" live-update affordance (Iris, PLANS/WEB_READER_LIVE_UPDATE_
// SPEC.md). A calm top-center paper pill — never a banner, never yanks the book.
// Behaviour:
//  - MID-READ (not on the cover) → show the pill; the current spread stays until
//    the viewer taps it. Whole pill taps → refresh. × dismisses.
//  - STILL ON THE COVER → auto-refresh silently + a 1.5s "Updated" micro-toast,
//    so nothing interrupts a read that hasn't started.
//  - Re-appears only on the NEXT distinct change (dismiss is remembered upstream).
type Mode = "hidden" | "pill" | "updating" | "toast";

/** Live-update wiring the readers thread down from App (useAlbum). */
export interface LiveProps {
  hasUpdate: boolean;
  onApply: () => void;
  onDismiss: () => void;
}

export function LiveUpdate({
  hasUpdate,
  atCover,
  onApply,
  onDismiss,
}: {
  hasUpdate: boolean;
  atCover: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const [mode, setMode] = useState<Mode>("hidden");
  const atCoverRef = useRef(atCover);
  atCoverRef.current = atCover;
  const prev = useRef(false);

  // React to the update flag's edges.
  useEffect(() => {
    if (hasUpdate && !prev.current) {
      // New change surfaced.
      if (atCoverRef.current) {
        onApply(); // haven't started reading → refresh silently
        setMode("toast");
      } else {
        setMode("pill"); // mid-read → offer, never yank
      }
    } else if (!hasUpdate && prev.current) {
      // Resolved (applied or dismissed) → retract the pill/spinner.
      setMode((m) => (m === "pill" || m === "updating" ? "hidden" : m));
    }
    prev.current = hasUpdate;
  }, [hasUpdate, onApply]);

  // Auto-dismiss the "Updated" toast.
  useEffect(() => {
    if (mode !== "toast") return;
    const t = window.setTimeout(() => setMode("hidden"), 1500);
    return () => window.clearTimeout(t);
  }, [mode]);

  if (mode === "hidden") return null;

  if (mode === "toast") {
    return (
      <div className="live live--toast" role="status" aria-live="polite">
        <Sparkle />
        <span>Updated</span>
      </div>
    );
  }

  const busy = mode === "updating";
  return (
    <div className="live" role="status" aria-live="polite">
      <button
        type="button"
        className="live__pill"
        onClick={() => {
          if (busy) return;
          setMode("updating");
          onApply();
        }}
        disabled={busy}
        aria-label="Load new photos"
      >
        {busy ? <Spinner /> : <Sparkle />}
        <span className="live__label">{busy ? "Updating…" : "New photos added"}</span>
        {!busy && <Refresh />}
      </button>
      {!busy && (
        <button
          type="button"
          className="live__x"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}

function Sparkle() {
  return (
    <svg className="live__ico" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="var(--accent)"
        d="M12 2.6l1.7 4.9 4.9 1.7-4.9 1.7L12 15.8l-1.7-4.9-4.9-1.7 4.9-1.7L12 2.6zM19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8L19 15z"
      />
    </svg>
  );
}

function Refresh() {
  return (
    <svg className="live__ico live__ico--refresh" viewBox="0 0 24 24" aria-hidden="true" fill="none">
      <path
        d="M20 11a8 8 0 1 0-.6 3"
        stroke="var(--accent)"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path d="M20 5.5V11h-5.5" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Spinner() {
  return <span className="live__spinner" aria-hidden="true" />;
}
